import Alert from '@components/alert';
import type { UploadedFile } from '@gurupay/gp-ui-library';
import {
	FileUploader,
	FileUploaderView,
	FileUploadStatus,
	SelectInput,
	TextArea,
	Typography,
} from '@gurupay/gp-ui-library';
import { useCreateMessage } from '@hooks/useCreateMessage';
import useGetTopics from '@hooks/useGetTopics';
import { useRejectedFilesNotify } from '@hooks/useRejectedFilesNotify.ts';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AdditionalInfoLinks from './AdditionalInfoLinks';
import { ALLOWED_MIMES } from './constants';
import DynamicFields from './DynamicFields';
import { MessageTfa } from './MessageTfa';
import type { ConfigProps } from './utils';

const EMPTY_STATE = {
	topic: '',
	content: {
		text: '',
	},
};

type FormValues = {
	topic: string;
	content: {
		text: string;
		period_start?: string;
		period_end?: string;
	};
};

export const NewMessage = () => {
	const { data: topics, isLoading: isTopicsLoading } = useGetTopics();
	const { t } = useTranslation('', { keyPrefix: 'messages' });
	const mutation = useCreateMessage();
	const [files, setFiles] = useState<File[]>([]);
	const [fields, setFields] = useState<ConfigProps | undefined>(undefined);
	const { t: tFileInput } = useTranslation('', { keyPrefix: 'fileInput' });
	const isFaqNeeded = fields?.faq;
	const isPriceListNeeded = fields?.pricelist;

	const topicOptions = topics?.map((topic) => ({
		value: topic.value,
		label: t(`topic.${topic.value}`),
	}));

	const {
		control,
		formState,
		watch,
		reset,
		handleSubmit,
		getValues,
		setValue,
		setError,
		clearErrors,
	} = useForm<FormValues>({
		mode: 'onChange',
		defaultValues: EMPTY_STATE,
	});

	const [signingInitiated, setSigningInitiated] = useState(false);
	const { errors } = formState;
	const topic = watch('topic');
	const startDate = watch('content.period_start');
	const endDate = watch('content.period_end');
	const hasDateError = !!startDate && !!endDate && startDate > endDate;
	const displayFiles: UploadedFile[] = files.map((f, index) => ({
		id: `${index}_${f.name}`,
		name: f.name,
		size: f.size,
		status: FileUploadStatus.SUCCESS,
	}));
	const removeFile = (id: string) => {
		setFiles((prev) => prev.filter((_, i) => i !== Number(id.split('_')[0])));
	};

	const { handleReject } = useRejectedFilesNotify({ size: 2 });

	useEffect(() => {
		if (!startDate || !endDate) {
			clearErrors(['content.period_start', 'content.period_end']);
			return;
		}

		if (startDate > endDate) {
			setError('content.period_start', {
				type: 'validate',
				message: t('errors.startDateError'),
			});
			setError('content.period_end', {
				type: 'validate',
				message: t('errors.endDateError'),
			});
		} else {
			clearErrors(['content.period_start', 'content.period_end']);
		}
	}, [startDate, endDate, setError, clearErrors, t]);

	useEffect(() => {
		if (topic) {
			reset({
				...EMPTY_STATE,
				topic,
			});
			setFields(topics?.find((item) => item.value === topic).additional_fields);
		}
	}, [topic, reset, topics]);

	const dynamicFieldEntries = useMemo<[string, ConfigProps][]>(
		() =>
			fields
				? (Object.entries(fields) as [string, ConfigProps][])
						.filter(([, v]) => !!v && typeof v === 'object' && 'type' in v)
						.sort(([, a], [, b]) => (a.order ?? 999) - (b.order ?? 999))
				: [],
		[fields],
	);

	return (
		<div>
			<Alert canClose className="my-space-40 max-mobile:my-space-24 mx-auto" type="info">
				{t('newMessage.infoAlert')}
			</Alert>
			<form className="flex flex-col items-center justify-center gap-y-space-16 max-w-[500px] mx-auto">
				<Controller
					control={control}
					name="topic"
					render={({ field }) => (
						<SelectInput
							{...field}
							className="ga-topic-selection"
							error={errors.topic?.message}
							isDisabled={signingInitiated}
							isLoading={isTopicsLoading}
							isRequired
							label={t('form.selectTopic')}
							options={topicOptions || []}
						/>
					)}
					rules={{ required: true }}
				/>
				{dynamicFieldEntries.length > 0 ? (
					<div className="w-full max-w-[500px] flex flex-col gap-y-space-16">
						{dynamicFieldEntries.map(([path, cfg]) => (
							<DynamicFields
								key={path}
								cfg={cfg}
								control={control}
								errors={errors}
								path={path}
								setValue={setValue}
								signingInitiated={signingInitiated}
							/>
						))}
					</div>
				) : (
					<div className="w-full max-w-[500px]">
						<Controller
							control={control}
							name="content.text"
							render={({ field }) => (
								<TextArea
									id="content.text"
									{...field}
									isDisabled={signingInitiated}
									isRequired
									label={t('form.message')}
								/>
							)}
							rules={{
								required: t('form.required'),
								minLength: {
									value: 4,
									message: t('form.minLength', { value: 4 }),
								},
							}}
						/>
					</div>
				)}

				<FileUploader
					className="w-full"
					files={displayFiles}
					fileTypes={ALLOWED_MIMES}
					hint={tFileInput('fileInfo')}
					isDisabled={signingInitiated}
					label={
						<p className="text-sm-medium">
							<span className="font-bold">{tFileInput('actionDesktop')} </span>
							{tFileInput('actionDesktopSmall')}
						</p>
					}
					maxFiles={5}
					maxSizeMB={2}
					onFileReject={handleReject}
					onFileRemove={removeFile}
					onFileSelect={(file) => setFiles((prev) => [...prev, ...file])}
					view={FileUploaderView.COMPACT}
				/>
			</form>

			<div className="flex justify-center mt-space-40 flex-col max-w-[500px] mx-auto">
				<AdditionalInfoLinks
					isFaqNeeded={isFaqNeeded}
					isPricelistNeeded={isPriceListNeeded}
				/>

				<Typography className="mb-space-40" level="h2">
					{t('newMessage.authInfoText')}
				</Typography>

				<MessageTfa
					afterSign={mutation.mutate}
					files={files}
					getValues={getValues}
					isLoading={mutation.isPending}
					isNewMessage
					isValidFields={formState.isValid && !hasDateError}
					onInitiateSigning={handleSubmit(() => {
						setSigningInitiated(true);
					})}
					setSigningInitiated={setSigningInitiated}
				/>
			</div>
		</div>
	);
};
